import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    Paper,
    TextField,
    MenuItem,
    InputAdornment,
    Tooltip,
    Card,
    CardContent,
    ToggleButtonGroup,
    ToggleButton,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
    Search,
    Clear,
    TrendingUp,
    TrendingDown,
    SwapHoriz,
    Undo,
    AccountBalanceWallet,
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ServerSideDataTable } from "../../components/common/DataTable";
import { paymentsOverviewService } from "../../services/paymentsOverviewService";
import { useAccounts } from "../../hooks/useAccounts";
import { formatCurrencyBDT } from "../../utils/currency";
import { ALL_PAYMENT_METHODS, getPaymentMethodColor } from "../../utils/paymentMethods";
import { PaymentMethod } from "../../types";
import {
    PaymentDirection,
    PaymentRecord,
    PaymentSearchParams,
    PaymentSummaryResponse,
} from "../../types/paymentsOverview";

// ─── Constants ───────────────────────────────────────────────────────────────

type DatePreset = "today" | "this_week" | "this_month" | "last_month" | "this_year" | "all";

interface DateRange {
    from: Date | null;
    to: Date | null;
}

function getPresetRange(preset: DatePreset): DateRange {
    const now = new Date();
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    switch (preset) {
        case "today": {
            const d = startOf(now);
            return { from: d, to: now };
        }
        case "this_week": {
            const day = now.getDay();
            const monday = new Date(now);
            monday.setDate(now.getDate() - ((day + 6) % 7));
            return { from: startOf(monday), to: now };
        }
        case "this_month":
            return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
        case "last_month": {
            const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const last = new Date(now.getFullYear(), now.getMonth(), 0);
            return { from: first, to: last };
        }
        case "this_year":
            return { from: new Date(now.getFullYear(), 0, 1), to: now };
        case "all":
        default:
            return { from: null, to: null };
    }
}

function toISODate(d: Date | null, endOfDay = false): string | undefined {
    if (!d) return undefined;
    const date = d.toISOString().split("T")[0];
    return endOfDay ? `${date}T23:59:59` : `${date}T00:00:00`;
}

const presets: { key: DatePreset; label: string }[] = [
    { key: "today", label: "Today" },
    { key: "this_week", label: "This Week" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
    { key: "this_year", label: "This Year" },
    { key: "all", label: "All Time" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    color: string;
    sub?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color, sub }) => (
    <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent sx={{ pb: "16px !important" }}>
            {/* Icon above the number on a phone, so "৳ 2,105.00" isn't cut to "৳ 2,10…". */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "flex-start", gap: { xs: 1, sm: 1.5 } }}>
                <Box
                    sx={{
                        bgcolor: `${color}.light`,
                        color: `${color}.dark`,
                        borderRadius: 1.5,
                        p: 0.75,
                        display: "flex",
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                    <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
                        {value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {label}
                    </Typography>
                    {sub && (
                        <Typography variant="caption" color="text.disabled">
                            {sub}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const PaymentsList: React.FC = () => {
    // ── List state ──────────────────────────────────────────────────────────
    const [payments, setPayments] = useState<PaymentRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalRows, setTotalRows] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    // ── Summary/KPI state ───────────────────────────────────────────────────
    const [summary, setSummary] = useState<PaymentSummaryResponse | null>(null);

    // ── Accounts (for the Account filter dropdown) ─────────────────────────
    const { accounts } = useAccounts();

    // ── Filter state ────────────────────────────────────────────────────────
    const [direction, setDirection] = useState<PaymentDirection>("all");
    const [searchText, setSearchText] = useState("");
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
    const [accountId, setAccountId] = useState("");
    const [amountMin, setAmountMin] = useState("");
    const [amountMax, setAmountMax] = useState("");
    const [refundOnly, setRefundOnly] = useState(false);
    const [channelType, setChannelType] = useState<"" | "OFFLINE" | "ONLINE">("");
    const [datePreset, setDatePreset] = useState<DatePreset>("this_month");
    const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });

    const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Derived filter params ────────────────────────────────────────────────
    const dateRange: DateRange =
        datePreset === "all" ? customRange : getPresetRange(datePreset);

    const searchParams: PaymentSearchParams = {
        direction,
        ...(searchText.trim() ? { search_query: searchText.trim() } : {}),
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
        ...(accountId ? { account_id: accountId } : {}),
        ...(amountMin ? { amount_min: parseFloat(amountMin) } : {}),
        ...(amountMax ? { amount_max: parseFloat(amountMax) } : {}),
        ...(refundOnly && direction !== "out" ? { is_refund: true } : {}),
        ...(channelType && direction !== "out" ? { order_channel_type: channelType } : {}),
        ...(dateRange.from ? { date_from: toISODate(dateRange.from) } : {}),
        ...(dateRange.to ? { date_to: toISODate(dateRange.to, true) } : {}),
    };

    // ── Data fetching ────────────────────────────────────────────────────────
    const fetchPayments = useCallback(
        async (params: PaymentSearchParams, page: number, pageSize: number) => {
            try {
                setLoading(true);
                setError("");
                const response = await paymentsOverviewService.search({
                    ...params,
                    skip: page * pageSize,
                    limit: pageSize,
                });
                setPayments(response.items);
                setTotalRows(response.total);
            } catch (err: any) {
                setError(err.response?.data?.detail || err.message || "Failed to load payments");
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const fetchSummary = useCallback(async (params: PaymentSearchParams) => {
        try {
            const data = await paymentsOverviewService.getSummary(params);
            setSummary(data);
        } catch {
            // KPI failure is non-critical — the table is still usable.
        }
    }, []);

    // ── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchPayments(searchParams, paginationModel.page, paginationModel.pageSize);
        fetchSummary(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        direction, searchText, paymentMethod, accountId, amountMin, amountMax,
        refundOnly, channelType, datePreset, customRange,
        paginationModel.page, paginationModel.pageSize,
    ]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleSearchChange = (val: string) => {
        if (searchDebounce.current) clearTimeout(searchDebounce.current);
        searchDebounce.current = setTimeout(() => setSearchText(val), 400);
    };

    const handlePreset = (preset: DatePreset) => {
        setDatePreset(preset);
        setPaginationModel((p) => ({ ...p, page: 0 }));
    };

    const handleDirectionChange = (val: PaymentDirection | null) => {
        if (!val) return;
        setDirection(val);
        if (val === "out") {
            setRefundOnly(false);
            setChannelType("");
        }
        setPaginationModel((p) => ({ ...p, page: 0 }));
    };

    const handleClearFilters = () => {
        setDirection("all");
        setSearchText("");
        setPaymentMethod("");
        setAccountId("");
        setAmountMin("");
        setAmountMax("");
        setRefundOnly(false);
        setChannelType("");
        setDatePreset("this_month");
        setCustomRange({ from: null, to: null });
        setPaginationModel({ page: 0, pageSize: 50 });
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns: GridColDef<PaymentRecord>[] = [
        {
            field: "date",
            headerName: "Date",
            width: 110,
            valueGetter: (value) => new Date(value).toLocaleDateString("en-GB"),
        },
        {
            field: "direction",
            headerName: "Direction",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value === "in" ? "In" : "Out"}
                    color={params.value === "in" ? "success" : "error"}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography
                        fontWeight={600}
                        color={params.value >= 0 ? "success.main" : "error.main"}
                        fontSize="0.875rem"
                    >
                        {params.value >= 0 ? "+" : "-"}
                        {formatCurrencyBDT(Math.abs(params.value))}
                    </Typography>
                    {params.row.is_refund && (
                        <Chip label="Refund" size="small" color="warning" sx={{ height: 18, fontSize: "0.65rem" }} />
                    )}
                </Box>
            ),
        },
        {
            field: "payment_method",
            headerName: "Method",
            width: 130,
            renderCell: (params) => (
                <Chip label={params.value} color={getPaymentMethodColor(params.value)} size="small" />
            ),
        },
        {
            field: "counterparty",
            headerName: "Order / Purchase",
            width: 140,
            valueGetter: (_value, row) => (row.direction === "in" ? row.order_number : row.purchase_number) || "—",
        },
        {
            field: "party",
            headerName: "Customer / Supplier",
            flex: 1,
            minWidth: 160,
            valueGetter: (_value, row) => (row.direction === "in" ? row.customer_name : row.supplier_name) || "—",
        },
        {
            field: "account_name",
            headerName: "Account",
            width: 140,
            valueGetter: (value) => value || "—",
        },
        {
            field: "reference_number",
            headerName: "Reference",
            width: 120,
            valueGetter: (value) => value || "—",
        },
        {
            field: "notes",
            headerName: "Notes",
            flex: 1,
            minWidth: 160,
            valueGetter: (value) => value || "—",
        },
    ];

    const hasActiveFilters =
        direction !== "all" || searchText || paymentMethod || accountId || amountMin || amountMax ||
        refundOnly || channelType || datePreset !== "this_month";

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h4" component="h1" fontWeight={700}>
                        Payments
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {/* KPI Cards */}
                {summary && (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <KpiCard
                                label="Total In"
                                value={formatCurrencyBDT(summary.total_in)}
                                sub={`${summary.in_count} payment(s)`}
                                icon={<TrendingUp fontSize="small" />}
                                color="success"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <KpiCard
                                label="Refunds"
                                value={formatCurrencyBDT(summary.total_refunds)}
                                sub={`${summary.refund_count} refund(s)`}
                                icon={<Undo fontSize="small" />}
                                color="warning"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <KpiCard
                                label="Total Out"
                                value={formatCurrencyBDT(summary.total_out)}
                                sub={`${summary.out_count} payment(s)`}
                                icon={<TrendingDown fontSize="small" />}
                                color="error"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <KpiCard
                                label="Net"
                                value={formatCurrencyBDT(summary.net)}
                                icon={<AccountBalanceWallet fontSize="small" />}
                                color={summary.net >= 0 ? "success" : "error"}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Filter bar */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {/* Direction — the headline filter */}
                    <Box sx={{ mb: 2 }}>
                        <ToggleButtonGroup
                            value={direction}
                            exclusive
                            size="small"
                            onChange={(_, val) => handleDirectionChange(val)}
                        >
                            <ToggleButton value="all">
                                <SwapHoriz fontSize="small" sx={{ mr: 0.75 }} />
                                All
                            </ToggleButton>
                            <ToggleButton value="in">
                                <TrendingUp fontSize="small" sx={{ mr: 0.75 }} />
                                Money In
                            </ToggleButton>
                            <ToggleButton value="out">
                                <TrendingDown fontSize="small" sx={{ mr: 0.75 }} />
                                Money Out
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Date presets */}
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                        {presets.map((p) => (
                            <Chip
                                key={p.key}
                                label={p.label}
                                size="small"
                                onClick={() => handlePreset(p.key)}
                                color={datePreset === p.key ? "primary" : "default"}
                                variant={datePreset === p.key ? "filled" : "outlined"}
                            />
                        ))}
                    </Box>

                    <Grid container spacing={1.5} alignItems="center">
                        {/* Text search */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search order#, purchase#, party, reference…"
                                defaultValue={searchText}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* Payment method */}
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Payment Method"
                                value={paymentMethod}
                                onChange={(e) => {
                                    setPaymentMethod(e.target.value as PaymentMethod | "");
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Methods</MenuItem>
                                {ALL_PAYMENT_METHODS.map((m) => (
                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Account */}
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Account"
                                value={accountId}
                                onChange={(e) => {
                                    setAccountId(e.target.value);
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                SelectProps={{ displayEmpty: true }}
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Accounts</MenuItem>
                                {accounts.map((a) => (
                                    <MenuItem key={a.account_id} value={a.account_id}>{a.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* Amount range */}
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label="Min Amount"
                                value={amountMin}
                                onChange={(e) => {
                                    setAmountMin(e.target.value);
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <TextField
                                type="number"
                                size="small"
                                fullWidth
                                label="Max Amount"
                                value={amountMax}
                                onChange={(e) => {
                                    setAmountMax(e.target.value);
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        {/* Custom date range */}
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <DatePicker
                                label="From"
                                value={dateRange.from}
                                onChange={(d) => {
                                    setDatePreset("all");
                                    setCustomRange((r) => ({ ...r, from: d }));
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                            <DatePicker
                                label="To"
                                value={dateRange.to}
                                onChange={(d) => {
                                    setDatePreset("all");
                                    setCustomRange((r) => ({ ...r, to: d }));
                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                }}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </Grid>

                        {/* In-only filters */}
                        {direction !== "out" && (
                            <>
                                <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Channel"
                                        value={channelType}
                                        onChange={(e) => {
                                            setChannelType(e.target.value as "" | "OFFLINE" | "ONLINE");
                                            setPaginationModel((p) => ({ ...p, page: 0 }));
                                        }}
                                        SelectProps={{ displayEmpty: true }}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value="">All Channels</MenuItem>
                                        <MenuItem value="OFFLINE">Offline</MenuItem>
                                        <MenuItem value="ONLINE">Online</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid size={{ xs: 6, sm: "auto" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={refundOnly}
                                                onChange={(e) => {
                                                    setRefundOnly(e.target.checked);
                                                    setPaginationModel((p) => ({ ...p, page: 0 }));
                                                }}
                                            />
                                        }
                                        label="Refunds only"
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Clear filters */}
                        {hasActiveFilters && (
                            <Grid size={{ xs: 12, sm: "auto" }}>
                                <Tooltip title="Clear all filters">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<Clear />}
                                        onClick={handleClearFilters}
                                    >
                                        Clear
                                    </Button>
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                </Paper>

                {/* Active filter chips */}
                {(paymentMethod || accountId || channelType || refundOnly) && (
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                        {paymentMethod && (
                            <Chip label={paymentMethod} size="small" onDelete={() => setPaymentMethod("")} color="secondary" variant="outlined" />
                        )}
                        {accountId && (
                            <Chip
                                label={accounts.find((a) => a.account_id === accountId)?.name || "Account"}
                                size="small" onDelete={() => setAccountId("")} color="primary" variant="outlined"
                            />
                        )}
                        {channelType && (
                            <Chip label={channelType} size="small" onDelete={() => setChannelType("")} color="info" variant="outlined" />
                        )}
                        {refundOnly && (
                            <Chip label="Refunds only" size="small" onDelete={() => setRefundOnly(false)} color="warning" variant="outlined" />
                        )}
                    </Box>
                )}

                {/* Table */}
                <ServerSideDataTable
                    rows={payments}
                    columns={columns}
                    loading={loading}
                    totalRows={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row: PaymentRecord) => row.id}
                    hideActions
                />
            </Box>
        </LocalizationProvider>
    );
};
